import { useEffect, useCallback } from 'react';

import { useUIStore } from '@/store/useUI';

/**
 * Hook for managing application theme
 *
 * Handles applying the theme to the document and respecting system preferences.
 * Theme preference is persisted in localStorage via the UI store.
 */
export function useTheme() {
  const theme = useUIStore((state) => state.theme);
  const setTheme = useUIStore((state) => state.setTheme);

  // Get the resolved theme (light or dark) based on current setting
  const resolvedTheme = useCallback((): 'light' | 'dark' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return theme;
  }, [theme]);

  // Apply theme to document
  useEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement;
      const resolved = resolvedTheme();

      if (resolved === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    applyTheme();

    // Sync Crisp chatbox color mode with app theme
    const resolved = resolvedTheme();
    window.$crisp?.push(['config', 'color:theme', ['blue']]);
    window.$crisp?.push(['config', 'color:mode', [resolved]]);

    // Listen for system theme changes when in 'system' mode
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme();

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, resolvedTheme]);

  return {
    theme,
    setTheme,
    resolvedTheme: resolvedTheme(),
  };
}
